import {AbstractFetcher} from '@/common/services/fetcher/AbstractFetcher';
import {MapBounds, MapLocationDialect, MapParams, MapWeatherPoint} from '@/app/interfaces/map';
import {LocationPoint, LocationSuperiorsList} from '@/common/interfaces/location';
import {alpha2ToAlpha3LanguageCode} from '@/common/constants/language';
import {parseLocationPoint} from '../helpers/locationHelpers';

export const getBoundariesGeoJson = (fetcher: AbstractFetcher) => (geoJsonId: string) => {
	return fetcher.fetch(`/api/map/location/boundaries?id=${geoJsonId}`, null, {method: 'GET'});
};

export const getLocation =
	(fetcher: AbstractFetcher) =>
	async (
		locationId: string,
		lang: string,
	): Promise<{
		location: LocationPoint;
		superior: LocationSuperiorsList;
		map: MapParams;
		dialect: MapLocationDialect;
	}> => {
		const response = await fetcher.fetch('/api/map/location/search/by_id', {
			lang: alpha2ToAlpha3LanguageCode[lang],
			location_id: locationId, // eslint-disable-line @typescript-eslint/naming-convention
		});

		return {
			location: parseLocationPoint(response.location),
			superior: {
				continent: response.superior?.continent ? parseLocationPoint(response.superior?.continent) : undefined,
				country: response.superior?.country ? parseLocationPoint(response.superior?.country) : undefined,
				region: response.superior?.region ? parseLocationPoint(response.superior?.region) : undefined,
				district: response.superior?.district ? parseLocationPoint(response.superior?.district) : undefined,
				subDistrict: response.superior?.sub_district
					? parseLocationPoint(response.superior?.sub_district)
					: undefined,
			},
			map: {
				bordersGeoJsonId: response.map.borders.geo_json_id,
				viewport: {
					center: response.map.viewport.center,
					zoom: response.map.viewport.zoom,
				},
			},
			dialect: {
				region: response.dialect?.region
					? {
							titlePc: response.dialect?.region.title_pc,
					  }
					: undefined,
				district: response.dialect?.district
					? {
							titlePc: response.dialect?.district.title_pc,
					  }
					: undefined,
				subDistrict: response.dialect?.sub_district
					? {
							titlePc: response.dialect?.sub_district.title_pc,
					  }
					: undefined,
			},
		};
	};

export const getPoints =
	(fetcher: AbstractFetcher) =>
	async (bounds: MapBounds, zoom: number, lang: string): Promise<MapWeatherPoint[]> => {
		const response = await fetcher.fetch('/api/map/point/search/by_bounds', {
			lang: alpha2ToAlpha3LanguageCode[lang],
			bounds,
			zoom,
		});

		return (response.points as any[]).map(({location, coordinates, weather, country, sun}) => ({
			location: parseLocationPoint(location),
			country,
			isNight: sun === 'night',
			coordinates: {
				lat: Number(coordinates.lat),
				lng: Number(coordinates.lng),
			},
			weather: {
				temp: weather.temp,
				tempWater: weather.temp_water,
				condition: weather.condition,
			},
		}));
	};
